import {
  AiAttachmentInfo,
  ApdmImportView,
  ApdmReadingAnswers,
} from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useEffect, useMemo } from 'react';
import {
  BehaviorSubject,
  EMPTY,
  Subject,
  from,
  merge,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  retry,
  switchMap,
  takeWhile,
} from 'rxjs/operators';
import {
  ApdmImportState,
  apdmImportStateFromView,
  isApdmImportSettled,
} from './importState';
import { newApdmImportRequestKey } from './newImportRequestKey';

const kPollMs = 1500;
const kMaxPollBackoffMs = 15000;

// The server refused or does not know the import: asking again won't help.
function isRefusal(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

export enum ApdmImportAction {
  Upload = 'upload',
  Answer = 'answer',
  Start = 'start',
  Delete = 'delete',
}

/**
 * One import, followed while the system works on it: the state polls until
 * it settles (summary, question, published, failed), and each action
 * answers with the import's new state and resumes following it.
 */
export function useApdmImport(initialImportId: string | null) {
  const { apdmService } = useServiceFactory();
  const importId$ = useCreation(
    () => new BehaviorSubject<string | null>(initialImportId),
    [initialImportId],
  );
  const follow$ = useCreation(() => new Subject<void>(), []);
  const state$ = useCreation(
    () => new BehaviorSubject<ApdmImportState | null>(null),
    [],
  );
  const pending$ = useCreation(
    () => new BehaviorSubject<ApdmImportAction | null>(null),
    [],
  );
  const failure$ = useCreation(
    () => new BehaviorSubject<string | null>(null),
    [],
  );

  useEffect(() => {
    const subscription = merge(
      importId$,
      follow$.pipe(map(() => importId$.getValue())),
    )
      .pipe(
        switchMap(importId =>
          importId === null
            ? of(null)
            : timer(0, kPollMs).pipe(
                exhaustMap(() => from(apdmService.getImport(importId))),
                map(apdmImportStateFromView),
                takeWhile(state => !isApdmImportSettled(state), true),
                // A network blip or a 5xx passes; keep asking, more slowly.
                retry({
                  delay: (error: unknown, count: number) =>
                    isRefusal(error)
                      ? throwError(() => error)
                      : timer(
                          Math.min(kPollMs * 2 ** count, kMaxPollBackoffMs),
                        ),
                  resetOnSuccess: true,
                }),
                // A refused or missing import will not come back by asking
                // again: say so and stop.
                catchError((error: unknown) => {
                  console.error('APDM import refresh failed', error);
                  failure$.next('这次导入没有加载出来，请刷新后重试。');
                  return EMPTY;
                }),
              ),
        ),
      )
      .subscribe(state => state$.next(state));
    return () => subscription.unsubscribe();
  }, [apdmService, importId$, follow$, state$, failure$]);

  const [state] = useBehaviorSubject(state$);
  const [pending] = useBehaviorSubject(pending$);
  const [failure] = useBehaviorSubject(failure$);

  const actions = useMemo(() => {
    const run = async (
      action: ApdmImportAction,
      work: () => Promise<ApdmImportView>,
    ): Promise<ApdmImportView | null> => {
      if (pending$.getValue()) return null;
      pending$.next(action);
      failure$.next(null);
      const actedOn = importId$.getValue();
      try {
        const view = await work();
        // The user may have opened another import meanwhile.
        if (importId$.getValue() === actedOn)
          state$.next(apdmImportStateFromView(view));
        return view;
      } catch (error) {
        // The import may have moved on without us: show where it is now.
        follow$.next();
        console.error('APDM import action failed', action, error);
        failure$.next(
          error instanceof Error && error.message
            ? error.message
            : '操作没有完成，请重试。',
        );
        return null;
      } finally {
        pending$.next(null);
      }
    };
    const current = () => importId$.getValue();
    return {
      open: (importId: string | null) => {
        failure$.next(null);
        state$.next(null);
        importId$.next(importId);
      },
      upload: async (attachment: AiAttachmentInfo) => {
        const openedBefore = current();
        const view = await run(ApdmImportAction.Upload, () =>
          apdmService.uploadImport(attachment.id),
        );
        // Opening another import meanwhile wins over the new upload.
        if (view && current() === openedBefore) {
          failure$.next(null);
          importId$.next(view.id);
        }
      },
      answer: async (answers: ApdmReadingAnswers) => {
        const importId = current();
        if (!importId) return;
        if (
          await run(ApdmImportAction.Answer, () =>
            apdmService.answerImport(importId, answers),
          )
        )
          follow$.next();
      },
      start: async () => {
        const importId = current();
        if (!importId) return;
        const requestKey = newApdmImportRequestKey();
        if (
          await run(ApdmImportAction.Start, () =>
            apdmService.startImport(importId, requestKey),
          )
        )
          follow$.next();
      },
      remove: async () => {
        const importId = current();
        if (!importId) return false;
        return !!(await run(ApdmImportAction.Delete, () =>
          apdmService.deleteImport(importId),
        ));
      },
    };
  }, [apdmService, importId$, follow$, state$, pending$, failure$]);

  return { state, pending, failure, actions };
}

export type ApdmImportController = ReturnType<typeof useApdmImport>;
